import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import useTenant from "../../../hooks/useTenant";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";
import FilterReset from "../../../components/FilterReset";
import { convertedValue } from "../../../utils/common";
import CustomTextField from "../../../components/CustomTextField";
import { useDialog } from "../../../contexts/DialogContext";
import CustomDatePicker from "../../../components/CustomDatePicker";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const statusOptions = [
  { label: "Pending", value: "PEN" },
  { label: "Success", value: "SUS" },
  { label: "Cancelled", value: "CAN" },
];

const ScheduleUserFilter = (props) => {
  const { unSetDialog } = useDialog();
  const { t } = useTranslation();

  const initialValues = {
    userName: "",
    tenantName: [],
    scheduleStatus: [],
    scheduleDate: [],
    scheduleDateTemp: "",
  };

  const { payload, setPayload, setFilterResetFunc, filterFormData, setFilterFormData } = props;

  const { autoSearchTenant, tenantAutoSearchResults, isLoadingAutoSearch } = useTenant();
  const [tenantPayload, setTenantPayload] = useState(currentPayload);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [defaultTenant, setDefaultTenant] = useState([]);
  const [defaultStatus, setDefaultStatus] = useState([]);

  const methods = useForm({
    defaultValues: filterFormData || initialValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    setFilterResetFunc(() => () => reset(initialValues));
  }, [reset]);

  const onSubmit = async (data) => {
    try {
      setFilterFormData(data);
      const controlledFields = ["userName", "tenantName", "scheduleStatus", "scheduleDate"];

      let filters = payload["filters"].filter((f) => !controlledFields.includes(f.fieldName));

      if (data.userName) {
        filters.push({
          fieldName: "userName",
          fieldValue: data.userName,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: true, // Mark as join because we implemented it in QueryBuilder
        });
      }

      if (data.tenantName && data.tenantName.length !== 0) {
        filters.push({
          fieldName: "tenantName",
          fieldValue: convertedValue(data.tenantName, "label").join(","),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: true,
        });
      }

      if (data.scheduleStatus && data.scheduleStatus.length !== 0) {
        filters.push({
          fieldName: "scheduleStatus",
          fieldValue: convertedValue(data.scheduleStatus, "value").join(","),
          op: "in",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }

      if (data.scheduleDate && data.scheduleDate.length !== 0) {
        filters.push({
          fieldName: "scheduleDate",
          fieldValue: data.scheduleDateTemp,
          op: "btn",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }

      setPayload({
        ...payload,
        filters: filters,
        pagination: { ...payload.pagination, pageNumber: 1 },
      });
      unSetDialog("schedule_user_filter");
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    autoSearchTenant(tenantPayload);
  }, [tenantPayload]);

  useEffect(() => {
    if (tenantAutoSearchResults.data.length !== 0) {
      setTenantOptions([...tenantOptions, ...tenantAutoSearchResults.data]);
    }
  }, [tenantAutoSearchResults]);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} pt={4}>
            <Grid item md={5} sm={6} xs={12}>
              <CustomTextField
                label={t("user_name")}
                name="userName"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
            <Grid item md={5} sm={6} xs={12}>
              <SearchableAutoComplete
                control={control}
                data={tenantOptions}
                label={t("franchise_name")}
                payload={tenantPayload}
                setPayload={setTenantPayload}
                loading={isLoadingAutoSearch}
                name="tenantName"
                multiple={true}
                defaultValue={defaultTenant}
                hasNextPage={tenantAutoSearchResults.paging.hasNextPage}
              />
            </Grid>
            <Grid item md={5} sm={6} xs={12}>
              <SearchableAutoComplete
                control={control}
                data={statusOptions}
                label="Status"
                name="scheduleStatus"
                multiple={true}
                defaultValue={defaultStatus}
              />
            </Grid>
            <Grid item md={5} sm={6} xs={12}>
              <CustomDatePicker
                name="scheduleDate"
                nameTemp="scheduleDateTemp"
                label={t("schedule_date")}
                range={true}
                control={control}
              />
            </Grid>
          </Grid>
          <FilterReset resetForm={() => reset(initialValues)} />
        </form>
      </FormProvider>
    </React.Fragment>
  );
};

export default ScheduleUserFilter;
